import React, { useState, useEffect } from 'react';
import { orcamentosApi, leadsApi } from '../lib/api';
import { formatCurrency, ORCAMENTO_STATUS_OPTIONS, PERIODOS_ATENDIMENTO } from '../lib/utils';
import { CidadeSelectOptions, ZonaBairroFields } from './CidadeSelectOptions';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { ServicoPicker } from './ui/ServicoPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, ChevronsUpDown, Check } from 'lucide-react';

const soDigitos = (s) => (s || '').replace(/\D/g, '');

const emptyItemOrc = () => ({ servico_id: '', servico_nome: '', quantidade: 1, valor_unitario: 0 });

const emptyOrcamento = {
  cliente_nome: '',
  cliente_telefone: '',
  cliente_endereco: '',
  cidade_atendimento: '',
  zona_atendimento: '',
  bairro_atendimento: '',
  itens: [emptyItemOrc()],
  descricao_servico: '',
  validade: '',
  observacoes: '',
  periodo_atendimento: '',
  status: 'Em andamento',
  desconto_percentual: 0
};

const validadePadrao = () => {
  const hoje = new Date();
  hoje.setDate(hoje.getDate() + 7);
  return hoje.toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
};

export const OrcamentoFormDialog = ({ open, onOpenChange, selected, prefill, leadId, servicos, cidadesList, clientes = [], onSaved }) => {
  const [formData, setFormData] = useState({ ...emptyOrcamento });
  const [saving, setSaving] = useState(false);
  const [clienteExistenteId, setClienteExistenteId] = useState('');
  const [clienteOpen, setClienteOpen] = useState(false);
  const [clienteBusca, setClienteBusca] = useState('');

  const clientesFiltrados = clientes
    .slice()
    .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''))
    .filter((c) => {
      const q = clienteBusca.trim().toLowerCase();
      if (!q) return true;
      const qd = soDigitos(clienteBusca);
      const nomeMatch = (c.nome || '').toLowerCase().includes(q);
      const telMatch = qd.length > 0 && soDigitos(c.telefone).includes(qd);
      return nomeMatch || telMatch;
    });

  const aplicarCliente = (c) => {
    setClienteExistenteId(c.id);
    const end = c.endereco || {};
    const partes = [end.rua, end.numero && `nº ${end.numero}`, end.complemento, end.bairro, end.cidade && end.estado ? `${end.cidade}/${end.estado}` : (end.cidade || end.estado)].filter(Boolean).join(', ');
    setFormData((prev) => ({
      ...prev,
      cliente_nome: c.nome || '',
      cliente_telefone: c.telefone || '',
      cliente_endereco: partes || prev.cliente_endereco,
      cidade_atendimento: end.cidade || prev.cidade_atendimento,
    }));
  };

  useEffect(() => {
    if (!open) return;
    setClienteExistenteId('');
    if (selected) {
      let itens = [emptyItemOrc()];
      if (Array.isArray(selected.itens) && selected.itens.length > 0) {
        itens = selected.itens.map((it) => ({
          servico_id: it.servico_id || '',
          servico_nome: it.servico_nome || '',
          quantidade: Number(it.quantidade || 1),
          valor_unitario: Number(it.valor_unitario || 0)
        }));
      } else if (selected.servico_id) {
        itens = [{ servico_id: selected.servico_id, servico_nome: selected.descricao_servico || '', quantidade: 1, valor_unitario: Number(selected.valor || 0) }];
      }
      setFormData({
        cliente_nome: selected.cliente_nome,
        cliente_telefone: selected.cliente_telefone,
        cliente_endereco: selected.cliente_endereco || '',
        cidade_atendimento: selected.cidade_atendimento || '',
        zona_atendimento: selected.zona_atendimento || '',
        bairro_atendimento: selected.bairro_atendimento || '',
        itens,
        descricao_servico: selected.descricao_servico || '',
        validade: selected.validade,
        observacoes: selected.observacoes || '',
        periodo_atendimento: selected.periodo_atendimento || '',
        status: selected.status,
        desconto_percentual: Number(selected.desconto_percentual || 0)
      });
    } else {
      setFormData({
        ...emptyOrcamento,
        itens: [emptyItemOrc()],
        validade: validadePadrao(),
        ...(prefill || {})
      });
    }
  }, [open, selected, prefill]);

  const updateOrcItem = (idx, patch) => {
    setFormData((prev) => ({ ...prev, itens: prev.itens.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  };

  const handleItemServicoChange = (idx, servicoId) => {
    const s = servicos.find((x) => x.id === servicoId);
    updateOrcItem(idx, { servico_id: servicoId, servico_nome: s?.nome || '', valor_unitario: s?.preco_base || 0 });
  };

  const addOrcItem = () => setFormData((prev) => ({ ...prev, itens: [...prev.itens, emptyItemOrc()] }));
  const removeOrcItem = (idx) => setFormData((prev) => {
    const next = prev.itens.filter((_, i) => i !== idx);
    return { ...prev, itens: next.length ? next : [emptyItemOrc()] };
  });

  const total = formData.itens.reduce((sum, it) => sum + (Number(it.valor_unitario || 0) * Number(it.quantidade || 1)), 0);
  const descontoPct = Math.max(0, Math.min(100, Number(formData.desconto_percentual || 0)));
  const totalComDesconto = Math.round(total * (1 - descontoPct / 100) * 100) / 100;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_nome.trim() || !formData.cliente_telefone.trim()) {
      toast.error('Nome e telefone do cliente são obrigatórios');
      return;
    }
    const itensValidos = formData.itens.filter((it) => it.servico_id);
    if (itensValidos.length === 0) {
      toast.error('Adicione ao menos 1 item com serviço selecionado');
      return;
    }
    if (!formData.validade) {
      toast.error('Informe a validade do orçamento');
      return;
    }
    setSaving(true);
    try {
      const { descricao_servico: _leg1, ...rest } = formData;
      const payload = {
        ...rest,
        descricao_servico: itensValidos.map((it) => `${it.servico_nome}${Number(it.quantidade || 1) > 1 ? ` (x${it.quantidade})` : ''}`).join(' + '),
        itens: itensValidos.map((it) => ({
          servico_id: it.servico_id,
          servico_nome: it.servico_nome,
          quantidade: Math.max(0.01, Number(it.quantidade || 1)),
          valor_unitario: Number(it.valor_unitario || 0)
        }))
      };
      if (selected) {
        await orcamentosApi.update(selected.id, payload);
        toast.success('Orçamento atualizado!');
        onSaved?.({ id: selected.id }, false);
      } else {
        const res = await orcamentosApi.create(payload);
        toast.success('Orçamento criado!');
        if (leadId && res?.data?.id) {
          try {
            await leadsApi.marcarConvertido(leadId, res.data.id);
          } catch (linkErr) {
            console.error('Error linking lead:', linkErr);
            toast.error('Orçamento criado, mas não foi possível vincular ao lead');
          }
        }
        onSaved?.(res.data, true);
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving orcamento:', error);
      toast.error('Erro ao salvar orçamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Work Sans' }}>
            {selected ? 'Editar Orçamento' : 'Novo Orçamento'}
          </DialogTitle>
          <DialogDescription>
            Informe nome e telefone do cliente, serviços, valores e validade.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!selected && clientes.length > 0 && !prefill?.cliente_nome && (
            <div className="space-y-1 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-testid="usar-cliente-cadastrado">
              <Label className="text-emerald-800 font-semibold text-sm">Usar cliente já cadastrado (opcional)</Label>
              <Popover open={clienteOpen} onOpenChange={setClienteOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="select-cliente-cadastrado">
                    {clienteExistenteId
                      ? (() => { const c = clientes.find((x) => x.id === clienteExistenteId); return c ? `${c.nome} — ${c.telefone}` : 'Novo cliente (preencher manualmente)'; })()
                      : 'Novo cliente (preencher manualmente)'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar por nome ou telefone..." value={clienteBusca} onValueChange={setClienteBusca} data-testid="cliente-busca-input" />
                    <CommandList>
                      <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="__none__"
                          onSelect={() => { setClienteExistenteId(''); setClienteOpen(false); setClienteBusca(''); }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${!clienteExistenteId ? 'opacity-100' : 'opacity-0'}`} />
                          — Novo cliente (preencher manualmente) —
                        </CommandItem>
                        {clientesFiltrados.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={c.id}
                            onSelect={() => { aplicarCliente(c); setClienteOpen(false); setClienteBusca(''); }}
                            data-testid={`cliente-opcao-${c.id}`}
                          >
                            <Check className={`mr-2 h-4 w-4 ${clienteExistenteId === c.id ? 'opacity-100' : 'opacity-0'}`} />
                            {c.nome} — {c.telefone}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cliente_nome">Nome do cliente *</Label>
              <Input id="cliente_nome" value={formData.cliente_nome} onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })} required data-testid="orcamento-nome-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cliente_telefone">Telefone *</Label>
              <Input id="cliente_telefone" value={formData.cliente_telefone} onChange={(e) => setFormData({ ...formData, cliente_telefone: e.target.value })} required data-testid="orcamento-telefone-input" />
            </div>
          </div>

          <div className="space-y-2 rounded-lg border p-3 bg-gray-50">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Itens do Orçamento *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addOrcItem} data-testid="add-orc-item-btn">
                <Plus size={14} className="mr-1" /> Adicionar item
              </Button>
            </div>
            <div className="space-y-2">
              {formData.itens.map((it, idx) => {
                const sub = Number(it.valor_unitario || 0) * Number(it.quantidade || 1);
                return (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded-md bg-white border" data-testid={`orc-item-${idx}`}>
                    <div className="col-span-12 md:col-span-5 space-y-1">
                      <Label className="text-xs">Serviço</Label>
                      <ServicoPicker servicos={servicos} value={it.servico_id || ''} onChange={(v) => handleItemServicoChange(idx, v)} placeholder="Selecione o serviço" testId={`orc-item-servico-select-${idx}`} />
                    </div>
                      {(() => {
                        const isM2 = servicos.find((s) => s.id === it.servico_id)?.unidade === 'm2';
                        return (
                          <div className="col-span-4 md:col-span-2 space-y-1">
                            <Label className="text-xs">{isM2 ? 'm²' : 'Qtde'}</Label>
                            <Input type="number" min={isM2 ? '0.1' : '1'} step={isM2 ? '0.1' : '1'} value={it.quantidade} onChange={(e) => updateOrcItem(idx, { quantidade: isM2 ? Math.max(0.1, parseFloat(e.target.value) || 0.1) : Math.max(1, parseInt(e.target.value, 10) || 1) })} data-testid={`orc-item-qtd-${idx}`} />
                          </div>
                        );
                      })()}
                    <div className="col-span-5 md:col-span-2 space-y-1">
                      <Label className="text-xs">Vlr unit.</Label>
                      <Input type="number" min="0" step="0.01" value={it.valor_unitario} onChange={(e) => updateOrcItem(idx, { valor_unitario: parseFloat(e.target.value) || 0 })} data-testid={`orc-item-vunit-${idx}`} />
                    </div>
                    <div className="col-span-3 md:col-span-2 space-y-1">
                      <Label className="text-xs">Subtotal</Label>
                      <div className="h-9 flex items-center px-2 rounded-md bg-gray-100 text-sm font-medium text-emerald-700 truncate">{formatCurrency(sub)}</div>
                    </div>
                    <div className="col-span-12 md:col-span-1 flex md:justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeOrcItem(idx)} disabled={formData.itens.length === 1} title="Remover item" data-testid={`orc-item-remove-${idx}`}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-sm">
              <div className="flex items-center gap-2">
                <Label htmlFor="desconto-pct" className="text-xs text-gray-500">Desconto (%)</Label>
                <Input
                  id="desconto-pct"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  className="w-24 h-8"
                  value={formData.desconto_percentual}
                  onChange={(e) => setFormData({ ...formData, desconto_percentual: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                  data-testid="orc-desconto-input"
                />
              </div>
              <div className="text-right">
                {descontoPct > 0 && (
                  <div className="text-xs text-gray-400">
                    <span className="line-through">{formatCurrency(total)}</span>
                    <span className="ml-1 text-red-500">−{String(descontoPct).replace('.', ',')}%</span>
                  </div>
                )}
                <span className="text-gray-500 mr-2">Total:</span>
                <span className="font-bold text-emerald-700 text-lg" data-testid="orc-total">{formatCurrency(totalComDesconto)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="cliente_endereco">Endereço do cliente</Label>
            <Input id="cliente_endereco" value={formData.cliente_endereco} onChange={(e) => setFormData({ ...formData, cliente_endereco: e.target.value })} placeholder="Rua, número, bairro, cidade" data-testid="orcamento-endereco-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Cidade</Label>
              <Select value={formData.cidade_atendimento || undefined} onValueChange={(v) => setFormData({ ...formData, cidade_atendimento: v, zona_atendimento: '' })}>
                <SelectTrigger data-testid="orcamento-cidade-select"><SelectValue placeholder="Selecione a cidade" /></SelectTrigger>
                <SelectContent>
                  <CidadeSelectOptions cidades={cidadesList} currentValue={formData.cidade_atendimento} />
                </SelectContent>
              </Select>
            </div>
            <ZonaBairroFields
              cidades={cidadesList}
              cidade={formData.cidade_atendimento}
              zona={formData.zona_atendimento}
              bairro={formData.bairro_atendimento}
              onZonaChange={(v) => setFormData({ ...formData, zona_atendimento: v })}
              onBairroChange={(v) => setFormData({ ...formData, bairro_atendimento: v })}
              testPrefix="orcamento"
            />
            <div className="space-y-1">
              <Label>Período</Label>
              <Select value={formData.periodo_atendimento || undefined} onValueChange={(v) => setFormData({ ...formData, periodo_atendimento: v })}>
                <SelectTrigger data-testid="orcamento-periodo-select"><SelectValue placeholder="Selecione o período" /></SelectTrigger>
                <SelectContent>
                  {PERIODOS_ATENDIMENTO.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label} ({p.horario})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="validade">Validade *</Label>
              <Input id="validade" type="date" value={formData.validade} onChange={(e) => setFormData({ ...formData, validade: e.target.value })} required data-testid="orcamento-validade-input" />
            </div>
          </div>

          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger data-testid="orcamento-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORCAMENTO_STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea id="observacoes" value={formData.observacoes} onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })} rows={2} data-testid="orcamento-obs-input" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="save-orcamento-btn">
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</>) : 'Salvar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
